import { LLMConstants } from "./constants";

/**
 * Whether a variable is a model's output, i.e. one of the Core AI Models or LLM
 * Models groups, as opposed to metadata or another step's output. Lives here
 * rather than with the chain source state so surfaces that only need the rule,
 * such as the save diff, can apply it without pulling in a store.
 */
export function isModelVariable(variableName: string): boolean {
    return (
        variableName.startsWith(
            `${LLMConstants.MLModelsFullyQualifiedName}.`,
        ) ||
        variableName.startsWith(`${LLMConstants.LLMModelsFullyQualifiedName}.`)
    );
}

/**
 * Whether a variable is another LLM model's output. Split out from
 * `isModelVariable` because the two model groups are not interchangeable
 * everywhere: publishing tolerates a Core AI dependency but not an LLM one.
 */
export function isLLMModelVariable(variableName: string): boolean {
    return variableName.startsWith(
        `${LLMConstants.LLMModelsFullyQualifiedName}.`,
    );
}

export interface StructuredOutputBinding {
    extendedMetadataClassifierId: string;
    extendedMetadataClassifierName: string;
    stepVariable: string;
    jsonPath: string;
}

export interface ModelOutputSource {
    classifierId: string;
    classifierName: string;
    structuredOutput?: StructuredOutputBinding;
}

interface LLMVariableDescriptor {
    label: string;
    name: string;
    type: LLMVariableType;
    values?: unknown[] | null;
    /** Durable model identity and optional structured-output persistence binding. */
    modelOutputSource?: ModelOutputSource;
    /** This output currently supports only presence-based run conditions. */
    presenceOnly?: boolean;
}

export function getLLMVariableNames(variable: LLMVariableDescriptor): string[] {
    return [variable.name];
}

export function matchesLLMVariableName(
    variable: LLMVariableDescriptor,
    name: string,
): boolean {
    return getLLMVariableNames(variable).includes(name);
}

const promptVariablePattern = /\{\{\s*([^{}]+?)\s*\}\}/g;

/** Returns normalized variable names from complete `{{variable}}` prompt tokens. */
export function getPromptVariableReferences(prompt: string): string[] {
    return [...prompt.matchAll(promptVariablePattern)].map((match) =>
        match[1].trim(),
    );
}

/**
 * Rewrites complete prompt tokens. Returning undefined preserves the original
 * token; any other string replaces it, including an empty string for removal.
 */
export function rewritePromptVariableReferences(
    prompt: string,
    replacementFor: (variableName: string) => string | undefined,
): string {
    return prompt.replace(promptVariablePattern, (token, rawName) => {
        const replacement = replacementFor(rawName.trim());
        return replacement === undefined ? token : replacement;
    });
}

export interface LLMVariable extends LLMVariableDescriptor {
    category: LLMVariableCategory;
}

export enum LLMVariableType {
    String,
    Boolean,
    DateTime,
}

export enum LLMVariableCategory {
    AudioMetadata,
    MachineLearning,
    ExtendedMetadata,
    LLM,
}

export type SlimLLMVariable = LLMVariableDescriptor;

export interface LLMVariableGroup {
    label: string;
    variables: SlimLLMVariable[];
}

export const LLM_MODELS_VARIABLE_GROUP_LABEL = "LLM Models";

/**
 * Keep ordinary scalar LLM results in the familiar LLM Models group. Structured
 * outputs need a producer-specific heading because their option labels describe
 * only the extended-metadata destination and schema property.
 */
export function groupVariablesByProducingLLMModel(
    variableGroups: LLMVariableGroup[],
): LLMVariableGroup[] {
    return variableGroups.flatMap((group) => {
        if (group.label !== LLM_MODELS_VARIABLE_GROUP_LABEL) return [group];

        const scalarVariables = group.variables.filter(
            (variable) => !variable.modelOutputSource?.structuredOutput,
        );
        const groupsByModel = new Map<string, LLMVariableGroup>();

        for (const variable of group.variables.filter(
            (candidate) => candidate.modelOutputSource?.structuredOutput,
        )) {
            const source = variable.modelOutputSource;
            const groupKey = source!.classifierId;
            const modelGroup = groupsByModel.get(groupKey) ?? {
                label: `LLM · ${source!.classifierName}`,
                variables: [],
            };

            modelGroup.variables.push(variable);
            groupsByModel.set(groupKey, modelGroup);
        }

        return [
            ...(scalarVariables.length > 0
                ? [
                      {
                          label: LLM_MODELS_VARIABLE_GROUP_LABEL,
                          variables: scalarVariables,
                      },
                  ]
                : []),
            ...Array.from(groupsByModel.values()),
        ];
    });
}

export function getDefaultVariableValue(type: LLMVariableType) {
    switch (type) {
        case LLMVariableType.String:
            return "";
        case LLMVariableType.Boolean:
            return true;
        case LLMVariableType.DateTime:
            return new Date();
    }
}
